/* eslint-disable react/jsx-props-no-spreading */

'use client';

import { useDebouncedCallback } from '@coop/hooks';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useState } from 'react';
import {
    Tab as AriaTab,
    TabList as AriaTabList,
    TabPanel as AriaTabPanel,
    Tabs as AriaTabs,
} from 'react-aria-components';

import styles from './Tabs.module.scss';

export const Root = (props: Omit<React.ComponentProps<typeof AriaTabs>, 'keyboardActivation'>) => {
    const { className, ...rest } = props;

    const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

    const handleResize = useDebouncedCallback(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 320) {
            setOrientation('vertical');
        } else {
            setOrientation('horizontal');
        }
    }, 300);

    useEffect(() => {
        handleResize();

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [handleResize]);

    return (
        <AriaTabs
            orientation={orientation}
            keyboardActivation="manual"
            {...rest}
            className={classNames(className)}
        >
            {props.children}
        </AriaTabs>
    );
};

export const List = (
    props: React.ComponentProps<typeof AriaTabList> & {
        fullWidth?: boolean;
        align?: 'left' | 'center' | 'right';
    },
) => {
    const { className, fullWidth = true, align = 'center', ...rest } = props;

    return (
        <AriaTabList
            {...rest}
            className={classNames(
                className,
                styles.TabList,
                fullWidth && styles.FullWidth,
                styles[`Position-${align}`],
            )}
        >
            {props.children}
        </AriaTabList>
    );
};

export const Panel = (
    props: Omit<React.ComponentProps<typeof AriaTabPanel>, 'shouldForceMount'> & {
        alwaysMountInDOM?: boolean;
    },
) => {
    return (
        <AriaTabPanel
            {...props}
            shouldForceMount={props.alwaysMountInDOM}
            className={classNames(props.className, styles.TabPanel)}
        >
            {props.children}
        </AriaTabPanel>
    );
};

export const Tab = (props: React.ComponentProps<typeof AriaTab>) => {
    return (
        <AriaTab {...props} className={classNames(props.className, styles.Tab)}>
            {props.children}
        </AriaTab>
    );
};

const Tabs = { Root, List, Panel, Tab };

export default Tabs;
